/**
 * Note Type Mapper - Utility functions for note type formatting and badge variants
 * 
 * Official Note Type ID Mappings:
 * 1  = Prior Authorization (PA)
 * 2  = Step Therapy (ST)
 * 4  = Quantity Limit (QL)
 * 8  = Other Restriction (OR)
 * 16 = Other Note (ON)
 * 32 = Conditional PA (CPA)
 * 64 = Age Restriction (AR)
 * 65 = Editor PA (FEPA)
 * 66 = Editor ST (FEST)
 * 67 = Prescriber Note (PN)
 */

/**
 * Get full name for note type abbreviation
 */
export function getNoteTypeFullName(noteTypeAbbr) {
  const noteTypeMap = {
    'PA': 'Prior Authorization',
    'ST': 'Step Therapy',
    'QL': 'Quantity Limit',
    'OR': 'Other Restriction',
    'ON': 'Other Note',
    'CPA': 'Conditional PA',
    'AR': 'Age Restriction',
    'FEPA': 'Editor PA',
    'FEST': 'Editor ST',
    'PN': 'Prescriber Note',
    'MP': 'Medical Policy Note',
    'SP': 'Specialty Pharmacy Note',
    'GP': 'General Policy',
  };
  return noteTypeMap[noteTypeAbbr] || noteTypeAbbr;
}

/**
 * Get badge variant for note type abbreviation
 */
export function getNoteTypeBadgeVariant(noteTypeAbbr) {
  switch (noteTypeAbbr) {
    case 'PA':
    case 'CPA':
    case 'FEPA':
      return 'destructive';
    case 'ST':
    case 'FEST':
      return 'default';
    case 'QL':
    case 'MP':
    case 'SP':
      return 'secondary';
    case 'OR':
    case 'ON':
    case 'AR':
    case 'PN':
    case 'GP':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Get badge variant and text for note type ID
 */
export function getNoteTypeBadgeInfo(noteTypeId) {
  switch (noteTypeId) {
    case 1:
      return { variant: 'destructive', text: 'PA' };
    case 2:
      return { variant: 'default', text: 'ST' };
    case 4:
      return { variant: 'secondary', text: 'QL' };
    case 8:
      return { variant: 'outline', text: 'OR' };
    case 16:
      return { variant: 'outline', text: 'ON' };
    case 32:
      return { variant: 'destructive', text: 'CPA' };
    case 64:
      return { variant: 'outline', text: 'AR' };
    case 65:
      return { variant: 'destructive', text: 'FEPA' };
    case 66:
      return { variant: 'default', text: 'FEST' };
    case 67:
      return { variant: 'outline', text: 'PN' };
    default:
      return { variant: 'outline', text: '' };
  }
}

/**
 * Get badge variant and text from full note type name
 * Handles text-based note types from API responses (medical notes)
 * Medical note types from API: 
 * - NoteTypeId 1 = "Medical Policy Note" → MP badge
 * - NoteTypeId 2 = "PA Note" → PA badge
 * - NoteTypeId 3 = "ST Note" → ST badge
 * - NoteTypeId 4 = "Specialty Pharmacy Note" → SP badge
 */
export function getBadgeInfoFromNoteName(noteName) {
  if (!noteName) return { variant: 'outline', text: '' };
  
  const normalizedName = noteName.toLowerCase().trim();
  
  // Map full note names to badge info
  if (normalizedName.includes('medical policy')) {
    return { variant: 'secondary', text: 'MP' };
  }
  if (normalizedName === 'pa note' || normalizedName === 'prior authorization') {
    return { variant: 'destructive', text: 'PA' };
  }
  if (normalizedName === 'st note' || normalizedName.includes('step therapy')) {
    return { variant: 'default', text: 'ST' };
  }
  if (normalizedName.includes('specialty pharmacy')) {
    return { variant: 'secondary', text: 'SP' };
  }
  if (normalizedName.includes('quantity limit')) {
    return { variant: 'secondary', text: 'QL' };
  }
  if (normalizedName.includes('general policy')) {
    return { variant: 'outline', text: 'GP' };
  }
  if (normalizedName.includes('conditional')) {
    return { variant: 'destructive', text: 'CPA' };
  }
  if (normalizedName.includes('age restriction')) {
    return { variant: 'outline', text: 'AR' };
  }
  if (normalizedName.includes('prescriber')) {
    return { variant: 'outline', text: 'PN' };
  }
  if (normalizedName.includes('other restriction')) {
    return { variant: 'outline', text: 'OR' };
  }
  if (normalizedName.includes('other note')) {
    return { variant: 'outline', text: 'ON' };
  }
  if (normalizedName.includes('editor') && normalizedName.includes('pa')) {
    return { variant: 'destructive', text: 'FEPA' };
  }
  if (normalizedName.includes('editor') && normalizedName.includes('st')) {
    return { variant: 'default', text: 'FEST' };
  }
  
  // Default fallback
  return { variant: 'outline', text: '' };
}

/**
 * Format note HTML - removes HTML tags and formats text
 */
export function formatNoteHTML(noteText) {
  if (!noteText) return '';
  
  // Remove HTML tags
  let formatted = noteText.replace(/<[^>]*>/g, '');
  
  // Decode HTML entities
  formatted = formatted
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Trim excessive whitespace
  formatted = formatted.trim();
  
  return formatted;
}
